import React from 'react';
import groupBy from 'lodash/groupBy';
import { Typography, Box } from '@mui/material';
import { PatientProfileInfoAccordionEditable } from './PatientProfileInfoAccordion';
import { useMedicalConditionsQuery, usePatientMedicalConditionsQuery } from '@uplift/queries';
import { useL10n } from '@uplift/hooks';
import { SelectListGroup, SelectListGroupItem } from '../../../../Inputs';
import { useAlert } from '../../../../AlertProvider';
import { MedicalCondition, MedicalConditionCategory, updatePatientMedicalConditions } from '@uplift/api';
import { useField } from 'formik';


export type MedicalConditionsAccordionProps = {
  patientId: string;
  editable: boolean;
  inlineForm?: boolean;
};


export type MedicalConditionsFormType = {
  cancerHistoryOther: string;
  medicalConditionIds: string[];
};


const labels = {
  cancer_history: 'Cancer History',
  cardiovascular_conditions: 'Cardiovascular Conditions',
  endocrine_and_metabolic_conditions: 'Endocrine and Metabolic Conditions',
  gastrointestinal_conditions: 'Gastrointestinal Conditions',
  infectious_diseases: 'Infectious Diseases',
  musculoskeletal_and_autoimmune_disorders: 'Musculoskeletal and Autoimmune Disorders',
  neurological_disorders: 'Neurological Disorders',
  reproductive_and_sexual_health: 'Reproductive and Sexual Health',
  respiratory_conditions: 'Respiratory Conditions',
  substance_use_and_related_conditions: 'Substance Use and Related Conditions',
};


const CancerHistoryPicker: React.VFC<{ conditions: MedicalCondition[] }> = ({ conditions }) => {
  const [ field ] = useField<string[]>('medicalConditionIds');
  const otherOption = React.useMemo(() => conditions.find(c => c.key === 'other'), [ conditions ]);
  const options = React.useMemo(() => {
    if (otherOption) {
      const filteredOptions = conditions.filter(c => c !== otherOption);

      return [ ...filteredOptions, otherOption ];
    }

    return conditions;
  }, [ otherOption, conditions ]);

  return (
    <SelectListGroupItem
      name="cancer_history"
      label={labels.cancer_history}
      options={options}
      getOptionLabel={i => i.name}
      other={{
        showInput: field.value.includes(otherOption?.id || ''),
        isOtherItem: (option) => option === otherOption,
        name: 'cancerHistoryOther',
      }}
    />
  );
};

export const MedicalConditionsForm: React.VFC = () => {
  const { data: medicalConditions = [] } = useMedicalConditionsQuery();
  const medicalConditionsGroupedByCategory = React.useMemo(() => groupBy(medicalConditions, 'category'), [ medicalConditions ]);

  return (
    <SelectListGroup name="medicalConditionIds">
      <CancerHistoryPicker
        conditions={medicalConditionsGroupedByCategory?.cancer_history || []}
      />

      {([
        'cardiovascular_conditions',
        'endocrine_and_metabolic_conditions',
        'gastrointestinal_conditions',
        'infectious_diseases',
        'musculoskeletal_and_autoimmune_disorders',
        'neurological_disorders',
        'reproductive_and_sexual_health',
        'respiratory_conditions',
        'substance_use_and_related_conditions',
      ] as MedicalConditionCategory[]).map(category => (
        <SelectListGroupItem
          key={category}
          name={category}
          label={labels[category]}
          options={medicalConditionsGroupedByCategory?.[category] || []}
          getOptionLabel={i => i.name}
        />
      ))}
    </SelectListGroup>
  );
};


export const MedicalConditionsAccordion: React.VFC<MedicalConditionsAccordionProps> = ({ patientId, editable, inlineForm }) => {
  const l10n = useL10n();
  const alert = useAlert();
  const { data: medicalConditions = [] } = useMedicalConditionsQuery();
  const { data: patientMedicalConditions = [], refetch } = usePatientMedicalConditionsQuery(patientId);

  const initialValues: MedicalConditionsFormType = {
    cancerHistoryOther: patientMedicalConditions.find(c => c.key === 'other')?.name || '',
    medicalConditionIds: patientMedicalConditions.map(medicalCondition => medicalCondition.id)
  };

  const onSubmit = async (values: MedicalConditionsFormType) => {
    const conditionById = medicalConditions.reduce((acc, medCond) => ({ ...acc, [medCond.id]: medCond }), {} as Record<string, MedicalCondition>);
    const conditions = values.medicalConditionIds.map(id => conditionById[id]).filter(Boolean).map(condition => {
      if (condition.key === 'other' && values.cancerHistoryOther) {
        return { ...condition, name: values.cancerHistoryOther };
      }
      return condition;
    }).map(cond => [ cond.id, cond.name ] as const);

    await updatePatientMedicalConditions(patientId, conditions);
    await refetch();
  };

  const content = React.useMemo(() => {
    if (!patientMedicalConditions.length) {
      return <Typography variant="body2" color="textPrimary">{l10n.clientProfile.medicalConditions.noMedicalConditions}</Typography>;
    }

    const grouped = groupBy(patientMedicalConditions, 'category');
    const groupedList = (Object.keys(grouped) as MedicalConditionCategory[]).map(category => ({
      category: category,
      items: grouped[category],
    }));

    return (
      <Box fontWeight={500} display="flex" flexDirection="column" gap={1}>
        {groupedList.map(group => (
          <Typography variant="body2" color="textPrimary" key={group.category}>
            <strong>{labels[group.category]}: </strong>
            {group.items.map(item => item.name).join(', ')}
          </Typography>
        ))}
      </Box>
    );
  }, [ patientMedicalConditions, l10n ]);

  return (
    <PatientProfileInfoAccordionEditable
      title="Medical Conditions"
      editable={editable}
      inlineForm={inlineForm}
      formikConfig={{
        initialValues,
        onSubmit,
      }}
      form={<MedicalConditionsForm />}
      onSuccess={(onCancelEdit) => {
        alert.success('Medical Conditions updated');
        onCancelEdit();
      }}
    >
      {content}
    </PatientProfileInfoAccordionEditable>
  );
};
